import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import { useErrorHandler } from '@/hooks/use-error-handler';
import graphqlClient from '@/graphql/graphql-client';
import { requestTemplatePages } from '@/graphql/queries/admin';
import {
  DELETE_TEMPLATE_PAGE_MUTATION,
  INSERT_TEMPLATE_PAGE_MUTATION,
  UPDATE_TEMPLATE_PAGE_MUTATION,
} from '@/graphql/mutations/admin';
import { generateLandingViaAgent } from '../services/template-pages.service';
import type {
  LandingGenerationResult,
  TemplatePageInsertInput,
  TemplatePageRecord,
  TemplatePageUpdateInput,
} from '../templates/types/landing-page.types';
import type { TemplateWithScreens } from '@/modules/templates/types/templates.types';

const KEY = (slug: string | undefined, locale: string | undefined) =>
  ['templatePages', slug ?? '', locale ?? ''] as const;

const getTemplatePageBySlug = async (
  slug: string,
  locale: string
): Promise<TemplatePageRecord | null> => {
  try {
    const response = await requestTemplatePages({
      where: { Slug: { eq: slug }, Locale: { eq: locale } },
      paging: { pageNo: 1, pageSize: 1 },
    });
    const items = response.getTemplatePagess.items ?? [];
    return (items[0] as TemplatePageRecord | undefined) ?? null;
  } catch (error) {
    console.error('Error fetching template page:', error);
    return null;
  }
};

export const useGetTemplatePageBySlug = (
  slug: string | undefined,
  locale: string | undefined
) => {
  return useGlobalQuery<TemplatePageRecord | null>({
    queryKey: KEY(slug, locale),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => getTemplatePageBySlug(slug!, locale!),
    enabled: !!slug && !!locale,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useCreateTemplatePage = (slug?: string, locale?: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: async (input: TemplatePageInsertInput) => {
      const existing = await getTemplatePageBySlug(input.Slug, input.Locale);
      if (existing) {
        throw new Error(
          `A landing page already exists for slug "${input.Slug}" and locale "${input.Locale}".`
        );
      }
      const response = await graphqlClient.request(INSERT_TEMPLATE_PAGE_MUTATION, input);
      const itemId = response.insertTemplatePages?.itemId;
      if (!itemId) {
        throw new Error('UDS did not return an itemId for the inserted template page.');
      }
      return itemId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY(slug, locale) });
      queryClient.invalidateQueries({ queryKey: ['templatePages'] });
      toast({ variant: 'success', title: 'Landing page created' });
    },
    onError: (error: Error) => {
      handleError(error, { defaultMessage: 'Failed to save landing page' });
    },
  });
};

export const useUpdateTemplatePage = (slug?: string, locale?: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: (variables: { itemId: string; input: TemplatePageUpdateInput }) =>
      graphqlClient.request(UPDATE_TEMPLATE_PAGE_MUTATION, {
        ItemId: variables.itemId,
        ...variables.input,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY(slug, locale) });
      queryClient.invalidateQueries({ queryKey: ['templatePages'] });
      toast({ variant: 'success', title: 'Landing page saved' });
    },
    onError: (error: Error) => {
      handleError(error, { defaultMessage: 'Failed to save landing page' });
    },
  });
};

export const useDeleteTemplatePage = (slug?: string, locale?: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: (itemId: string) =>
      graphqlClient.request(DELETE_TEMPLATE_PAGE_MUTATION, {
        itemId,
        isHardDelete: true,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY(slug, locale) });
      queryClient.invalidateQueries({ queryKey: ['templatePages'] });
      toast({ variant: 'success', title: 'Landing page deleted' });
    },
    onError: (error: Error) => {
      handleError(error, { defaultMessage: 'Failed to delete landing page' });
    },
  });
};

interface GenerateLandingArgs {
  template: TemplateWithScreens;
  locale: string;
  otherTemplates?: Array<{ slug: string; title: string; description?: string | null }>;
  signal?: AbortSignal;
}

export const useGenerateLandingPage = () => {
  const { handleError } = useErrorHandler();

  return useMutation<LandingGenerationResult, Error, GenerateLandingArgs>({
    mutationFn: (args) => generateLandingViaAgent(args),
    onError: (error: Error) => {
      if (error?.name === 'AbortError') return;
      handleError(error, { defaultMessage: 'Failed to generate landing page' });
    },
  });
};
